import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Icon } from "@/components/ui/icon";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { checkTitleLength, checkDescriptionLength, checkOgImage, SeoHealthIssue } from "@/lib/seo";

interface HealthCheckResult {
  entity: string;
  type: string;
  issues: SeoHealthIssue[];
}

export const SeoHealthChecksTab = () => {
  const [results, setResults] = useState<HealthCheckResult[]>([]);
  const [hasRun, setHasRun] = useState(false);

  const { data: overrides, isLoading: overridesLoading } = useQuery({
    queryKey: ['seo-overrides-health'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('seo_overrides')
        .select('*');
      if (error) throw error;
      return data;
    },
  });

  const { data: projects, isLoading: projectsLoading } = useQuery({
    queryKey: ['projects-health'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projects')
        .select('id, project_name, description, image_url');
      if (error) throw error;
      return data;
    },
  });

  const { data: globalSettings, isLoading: globalLoading } = useQuery({
    queryKey: ['seo-global-health'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('seo_global_settings')
        .select('*')
        .single();
      if (error && error.code !== 'PGRST116') throw error;
      return data;
    },
  });

  const isLoading = overridesLoading || projectsLoading || globalLoading;

  const runHealthCheck = () => {
    const checkResults: HealthCheckResult[] = [];
    const seenTitles = new Map<string, string[]>();

    // Check global settings
    const globalIssues: SeoHealthIssue[] = [];
    if (!globalSettings?.site_name) {
      globalIssues.push({ type: 'warning', message: 'Site name not configured', field: 'site_name' });
    }
    const descIssue = checkDescriptionLength(globalSettings?.default_meta_description || '');
    if (descIssue) globalIssues.push({ ...descIssue, field: 'default_meta_description' });
    const ogIssue = checkOgImage(globalSettings?.default_og_image_url || undefined);
    if (ogIssue) globalIssues.push(ogIssue);

    if (globalIssues.length > 0) {
      checkResults.push({
        entity: 'Global Settings',
        type: 'global',
        issues: globalIssues,
      });
    }

    // Check resume page
    const resumeOverride = overrides?.find(o => o.entity_type === 'resume');
    const resumeTitle = resumeOverride?.seo_title || '';
    const resumeDesc = resumeOverride?.seo_description || globalSettings?.default_meta_description || '';
    const resumeOgImage = resumeOverride?.og_image_url || globalSettings?.default_og_image_url;
    
    const resumeIssues: SeoHealthIssue[] = [];
    const titleIssue = checkTitleLength(resumeTitle);
    if (titleIssue) resumeIssues.push(titleIssue);
    const resumeDescIssue = checkDescriptionLength(resumeDesc);
    if (resumeDescIssue) resumeIssues.push(resumeDescIssue);
    const resumeOgIssue = checkOgImage(resumeOgImage || undefined);
    if (resumeOgIssue) resumeIssues.push(resumeOgIssue);

    if (resumeTitle) {
      if (seenTitles.has(resumeTitle)) {
        seenTitles.get(resumeTitle)!.push('Resume');
      } else {
        seenTitles.set(resumeTitle, ['Resume']);
      }
    }

    if (resumeIssues.length > 0) {
      checkResults.push({
        entity: 'Resume',
        type: 'resume',
        issues: resumeIssues,
      });
    }

    // Check projects
    projects?.forEach(project => {
      const override = overrides?.find(o => o.entity_id === project.id);
      const title = override?.seo_title || project.project_name;
      const desc = override?.seo_description || project.description || '';
      const ogImage = override?.og_image_url || project.image_url || globalSettings?.default_og_image_url;

      const projectIssues: SeoHealthIssue[] = [];
      const pTitleIssue = checkTitleLength(title);
      if (pTitleIssue) projectIssues.push(pTitleIssue);
      const pDescIssue = checkDescriptionLength(desc);
      if (pDescIssue) projectIssues.push(pDescIssue);
      const pOgIssue = checkOgImage(ogImage || undefined);
      if (pOgIssue) projectIssues.push(pOgIssue);

      if (title) {
        if (seenTitles.has(title)) {
          seenTitles.get(title)!.push(project.project_name);
        } else {
          seenTitles.set(title, [project.project_name]);
        }
      }

      if (projectIssues.length > 0) {
        checkResults.push({
          entity: project.project_name,
          type: 'project',
          issues: projectIssues,
        });
      }
    });

    // Check for duplicate titles
    seenTitles.forEach((entities, title) => {
      if (entities.length > 1) {
        checkResults.push({
          entity: `Duplicate Title: "${title}"`,
          type: 'duplicate',
          issues: [{
            type: 'error',
            message: `Found on: ${entities.join(', ')}`,
            field: 'seo_title',
          }],
        });
      }
    });

    setResults(checkResults);
    setHasRun(true);
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-32 w-full" />
        </CardContent>
      </Card>
    );
  }

  const totalIssues = results.reduce((sum, r) => sum + r.issues.length, 0);
  const errorCount = results.reduce((sum, r) => sum + r.issues.filter(i => i.type === 'error').length, 0);
  const warningCount = results.reduce((sum, r) => sum + r.issues.filter(i => i.type === 'warning').length, 0);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>SEO Health Check</CardTitle>
          <CardDescription>
            Run local validations to identify potential SEO issues
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={runHealthCheck} className="mb-4">
            <Icon name="play_arrow" size={16} className="mr-2" />
            Run Health Check
          </Button>

          {hasRun && (
            <div className="grid grid-cols-3 gap-4 mb-6">
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-foreground">{totalIssues}</p>
                  <p className="text-sm text-muted-foreground">Total Issues</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-destructive">{errorCount}</p>
                  <p className="text-sm text-muted-foreground">Errors</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-yellow-500">{warningCount}</p>
                  <p className="text-sm text-muted-foreground">Warnings</p>
                </CardContent>
              </Card>
            </div>
          )}

          {hasRun && results.length === 0 && (
            <div className="text-center py-8">
              <Icon name="check_circle" size={48} className="text-primary mx-auto mb-4" />
              <p className="text-lg font-medium text-foreground">All checks passed!</p>
              <p className="text-sm text-muted-foreground">No SEO issues detected</p>
            </div>
          )}

          {hasRun && results.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Entity</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Issues</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {results.map((result, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="font-medium">{result.entity}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">{result.type}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        {result.issues.map((issue, issueIdx) => (
                          <div key={issueIdx} className="flex items-center gap-2">
                            <Badge 
                              variant={issue.type === 'error' ? 'destructive' : 'secondary'}
                              className={issue.type === 'warning' ? 'bg-yellow-500/10 text-yellow-600' : ''}
                            >
                              {issue.type}
                            </Badge>
                            <span className="text-sm text-muted-foreground">{issue.message}</span>
                          </div>
                        ))}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SeoHealthChecksTab;
